/**
 * Sales page: list marketplace sales from listing_sales table.
 * Uses expandable card (FormSectionCard) and existing theme; filter and data shared via app store / React Query.
 */
import React, { useState } from 'react';
import { Container, Table, Spinner, Form } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../store/appStore';
import FormSectionCard from '../FormSectionCard';
import { getListingSales } from '../../services/listingSalesService';

function formatDate(value) {
  if (value == null) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function formatNumber(value) {
  if (value == null) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString();
}

const SalesPage = () => {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState(true);
  const productNameFilter = useAppStore((s) => s.salesPage.productNameFilter);
  const setSalesPage = useAppStore((s) => s.setSalesPage);

  const { data, isLoading, error } = useQuery({
    queryKey: ['listing-sales', productNameFilter],
    queryFn: () => getListingSales(token, { product_name: productNameFilter || undefined }),
    enabled: !!token
  });

  const sales = data?.listing_sales ?? [];

  const headerAction = (
    <Form.Group className="mb-0 d-flex align-items-center gap-2">
      <Form.Label className="mb-0 small text-nowrap">Product name</Form.Label>
      <Form.Control
        type="text"
        placeholder="Filter…"
        value={productNameFilter}
        onChange={(e) => setSalesPage((prev) => ({ ...prev, productNameFilter: e.target.value }))}
        size="sm"
        style={{ minWidth: '10rem', maxWidth: '16rem' }}
        aria-label="Filter by product name"
      />
    </Form.Group>
  );

  return (
    <div className="page-dark">
      <Container>
        <h1 className="page-title">Sales</h1>
        <p className="page-subtitle">Marketplace sales from listing_sales.</p>

        <div className="row justify-content-center">
          <div className="col-12 mb-4">
            <FormSectionCard
              title="Listing sales"
              expanded={expanded}
              onToggle={() => setExpanded((e) => !e)}
              headerAction={headerAction}
            >
              <div className="sales-table-card">
                {isLoading && (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" variant="light" aria-label="Loading sales" />
                  </div>
                )}
                {error && (
                  <p className="text-warning mb-0">{error.message}</p>
                )}
                {!isLoading && !error && (
                  <div className="table-responsive">
                    <Table bordered hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Id</th>
                          <th>Listing id</th>
                          <th>Product name</th>
                          <th>Remaining</th>
                          <th>Order id</th>
                          <th>Qty</th>
                          <th>Unit price</th>
                          <th>Total</th>
                          <th>Currency</th>
                          <th>Order status</th>
                          <th>Sale date</th>
                          <th>Payment status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="text-muted text-center py-4">
                              No sales yet.
                            </td>
                          </tr>
                        ) : (
                          sales.map((row) => (
                            <tr key={row.id}>
                              <td>{row.id}</td>
                              <td>{formatNumber(row.listing_id)}</td>
                              <td>{row.product_name ?? '—'}</td>
                              <td>{formatNumber(row.remaining_available)}</td>
                              <td>{row.marketplace_order_id ?? '—'}</td>
                              <td>{formatNumber(row.quantity)}</td>
                              <td>{formatNumber(row.unit_price)}</td>
                              <td>{formatNumber(row.total_amount)}</td>
                              <td>{row.currency_id ?? '—'}</td>
                              <td>{row.order_status ?? '—'}</td>
                              <td>{formatDate(row.sale_date)}</td>
                              <td>{row.payment_status ?? '—'}</td>
                              <td>{formatDate(row.created_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </FormSectionCard>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SalesPage;
